import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { useQueueAlertSettings } from '@/hooks/useQueueAlertSettings';
import InquiryAnchoredPopover from './InquiryAnchoredPopover';
import QueueAlertSettingsPanel from './QueueAlertSettingsPanel';
import styles from './InquiryAlertSettingsButton.module.scss';

const REVEALED_KEY = 'inquiries.alerts.revealed';

/**
 * Whether this browser has already been shown where the alert settings are.
 *
 * Per browser, and deliberately so: the settings themselves are per browser
 * (useQueueAlertSettings writes to localStorage, and the panel says so out loud), so a new
 * browser is a new place where the chime has never been configured and nobody has been told
 * where to configure it.
 */
const wasRevealed = (): boolean => {
  try {
    return localStorage.getItem(REVEALED_KEY) === '1';
  } catch {
    // Private mode, or storage refused. Treating that as "already shown" is the quieter mistake:
    // a panel that opens itself on every single visit is worse than one that never does.
    return true;
  }
};

/**
 * Recorded when the panel CLOSES, not when it opens.
 *
 * ⚠️ Not a detail. Writing it on open made the reveal fail in development and work in production,
 * which is the worst way for it to fail: StrictMode mounts a component twice, so the first mount
 * wrote the flag and opened the panel, the component was then torn down - taking the open state
 * with it - and the second mount read "already shown" and opened nothing. Deciding from the flag on
 * every mount and writing it on close is idempotent, so both mounts agree.
 *
 * It also reads better: "we have shown you this" is true once someone has actually dismissed it,
 * not the instant it appeared on screen.
 */
const markRevealed = (): void => {
  try {
    localStorage.setItem(REVEALED_KEY, '1');
  } catch {
    // Nothing to fall back to; the reveal simply happens again next time.
  }
};

/**
 * The alert settings for inquiries - sound, volume, and the desktop notification - reached from
 * the inquiries screen itself.
 *
 * ⚠️ These used to be a small bell in the site header, beside the queue capsule. They moved here
 * when the updates bell took over the bell glyph in that header (HAT-1099): two bells forty pixels
 * apart, one meaning "you have something new" and the other "sound is on", is a guess rather than
 * a control.
 *
 * The bell glyph comes BACK on this screen, though, and deliberately. The collision was with the
 * updates bell, which lives only in the header - here there is nothing to confuse it with, every
 * control on the screen is already about inquiries, and a struck-through bell says "muted" at a
 * glance in a way no gear can. A gear was the first attempt and it was worse twice over: it says
 * "settings" without saying whose, and the office board already has one leading to the office's
 * own settings screen, so it put two identical gears side by side.
 *
 * ⚠️ The header placement existed for a real reason, and dropping it would have brought that fault
 * back: a coordinator hears a chime and has nowhere to go to stop it. The answer is the reveal
 * below - the panel opens itself once, on the first visit to this screen. Every route that leads to
 * an inquiry lands here, the row-level "open inquiry" button included, so there is no way to be in
 * a conversation without having passed through this screen at least once.
 */
export default function InquiryAlertSettingsButton() {
  const { t } = useTranslation();
  const { settings } = useQueueAlertSettings();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  /* Whether the panel on screen opened by itself. A reveal has to survive the stray click that
     page load produces; a panel the user asked for should close as any popover does. */
  const [isReveal, setIsReveal] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  /*
   * The one-time reveal. Runs after mount so the button is measured and the popover has something
   * to anchor to; opening it before that put the panel in the corner of the screen.
   */
  useEffect(() => {
    if (wasRevealed() || !buttonRef.current) return;

    setIsReveal(true);
    setAnchor(buttonRef.current);
  }, []);

  /* Any close counts as having been shown - including a close after the user opened it themselves,
     which means they have found it and no longer need to be told where it is. */
  const close = () => {
    markRevealed();
    setIsReveal(false);
    setAnchor(null);
  };

  /* Whether anything would actually be heard or seen, not just the sound switch: someone who turned
     both off should not be looking at a live-looking bell. */
  const isSilent = !settings.sound && !settings.desktopNotification;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={styles.alertButton}
        onClick={(e) => {
          if (anchor) {
            close();
            return;
          }
          setIsReveal(false);
          setAnchor(e.currentTarget);
        }}
        title={t('inquiries.alerts.settingsTitle')}
        aria-label={t('inquiries.alerts.settingsTitle')}
      >
        {isSilent ? <NotificationsOffIcon sx={{ fontSize: 20 }} /> : <NotificationsActiveIcon sx={{ fontSize: 20 }} />}
      </button>

      <InquiryAnchoredPopover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={close}
        side="bottom"
        align="end"
        dismissOnInteractOutside={!isReveal}
      >
        <QueueAlertSettingsPanel onClose={close} />
      </InquiryAnchoredPopover>
    </>
  );
}
